import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

// Types pour la navigation
interface NavItem {
  name: string;
  href: string;
  icon: string;
  badge?: number;
  children?: NavItem[];
  roles?: string[]; // Si défini, visible uniquement pour ces rôles
}

// Configuration de la navigation (à adapter selon les rôles)
const navigationConfig: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: '📊',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Ma Boutique',
    href: '/settings',
    icon: '🏪',
    roles: ['owner', 'admin'],
  },
  {
    name: 'Publications',
    href: '/publications',
    icon: '📢',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Toutes les publications', href: '/publications', icon: '📋' },
      { name: 'Nouvelle publication', href: '/publications/create', icon: '➕' },
    ],
  },
  {
    name: 'Catalogue',
    href: '/catalog',
    icon: '📦',
    roles: ['owner', 'admin', 'manager', 'vendor'],
    children: [
      { name: 'Produits', href: '/products', icon: '📦' },
      { name: 'Promos DLC', href: '/promos-dlc', icon: '🏷️' },
      { name: 'Nouveautés', href: '/nouveautes', icon: '✨' },
      { name: 'Étiquettes de la semaine', href: '/etiquettes-semaine', icon: '🏷️' },
      { name: 'Catégories', href: '/categories', icon: '🏷️' },
      { name: 'Stocks & Inventaire', href: '/inventory', icon: '📊' },
      { name: 'Sorties de stock', href: '/stock-outs', icon: '📉' },
    ],
  },
  {
    name: 'Production',
    href: '/production',
    icon: '🌱',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Vue d\'ensemble', href: '/production', icon: '📊' },
      { name: 'Parcellaire', href: '/production/zones', icon: '🗺️' },
      { name: 'Variétés & Semences', href: '/production/varieties', icon: '🌿' },
      { name: 'Plan de culture', href: '/production/planning', icon: '📅' },
      { name: 'Journal des interventions', href: '/production/journal', icon: '📖' },
      { name: 'Achats d\'intrants', href: '/production/inputs', icon: '🧾' },
    ],
  },
  {
    name: 'Stockage',
    href: '/storage/locations',
    icon: '📍',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Emplacements', href: '/storage/locations', icon: '📍' },
      { name: 'Rangement', href: '/storage/putaway', icon: '📥' },
    ],
  },
  {
    name: 'Réception Fournisseurs',
    href: '/deliveries',
    icon: '📥',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Retouche Photos', href: '/photo-retouching', icon: '🖼️' },
      { name: 'Livraisons à Vérifier', href: '/deliveries', icon: '📋' },
      { name: 'Gestion Réserve (Lots)', href: '/backroom-management', icon: '📦' },
      { name: 'Templates d\'Étiquettes', href: '/label-templates', icon: '🏷️' },
    ],
  },
  {
    name: 'Ventes',
    href: '/sales',
    icon: '🛒',
    roles: ['owner', 'admin', 'manager', 'vendor'],
    children: [
      { name: 'Synthèse Commandes', href: '/orders-summary', icon: '📊' },
      { name: 'Caisse (POS)', href: '/pos', icon: '🛒' },
      { name: 'Commandes E-commerce', href: '/orders', icon: '📋' },
      { name: 'Préparation Commandes', href: '/preparation', icon: '📦' },
      { name: 'Précommandes', href: '/preorders-overview', icon: '🗓️' },
      { name: 'Types de précommande', href: '/preorder-types', icon: '⚙️' },
      { name: 'Historique', href: '/sales-history', icon: '📜' },
      { name: 'Rapports Z', href: '/closing-reports', icon: '📋' },
      { name: 'Factures Enfact', href: '/invoices', icon: '📄' },
      { name: 'Import Enfact', href: '/imports', icon: '📥' },
    ],
  },
  {
    name: 'Live Shopping',
    href: '/live-shopping',
    icon: '📹',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Dashboard', href: '/live-shopping', icon: '📊' },
      { name: 'Messages', href: '/messaging', icon: '💬' },
    ],
  },
  {
    name: 'Livraisons Clients',
    href: '/customer-deliveries',
    icon: '🚚',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Gestion des Tournées', href: '/customer-deliveries', icon: '📋' },
      { name: 'Tournées en cours', href: '/delivery-dashboard', icon: '📍' },
      { name: 'Livreurs', href: '/team?tab=drivers', icon: '👨‍✈️' },
      { name: 'SMS du livreur', href: '/customer-deliveries/sms', icon: '💬' },
      { name: 'Carnet de route', href: '/customer-deliveries/carnet', icon: '🚐' },
    ],
  },
  {
    name: 'Consignes/Vidanges',
    href: '/packaging',
    icon: '📦',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Clients',
    href: '/customers',
    icon: '👥',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Bons cadeaux',
    href: '/bons-cadeaux',
    icon: '🎁',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: '📈',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Rapport E-com',
    href: '/ecom-reports',
    icon: '📊',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Relevé ventes',
    href: '/releve-mensuel',
    icon: '🧮',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Rentabilité e-shop',
    href: '/rentabilite-eshop',
    icon: '💶',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'Gestion Équipe',
    href: '/team',
    icon: '👷',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Worksheets', href: '/worksheets', icon: '📋' },
      { name: 'Interface Exécutant', href: '/executor', icon: '📱' },
      { name: 'Équipe', href: '/team', icon: '👥' },
    ],
  },
  {
    name: 'Configuration',
    href: '/baker-closures',
    icon: '⚙️',
    roles: ['owner', 'admin', 'manager'],
    children: [
      { name: 'Fermetures Boulanger', href: '/baker-closures', icon: '📅' },
    ],
  },
];

export default function LayoutV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [userMenuOpen, setUserMenuOpen] = useState(false);


  // Fermer la sidebar à la navigation (mobile)
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);


  // Mapper le rôle AuthContext vers les rôles du menu
  const userRole = user?.role.toLowerCase() || 'admin';

  // Filtrer la navigation selon le rôle
  const filteredNav = navigationConfig.filter(
    (item) => !item.roles || item.roles.includes(userRole)
  );

  // L'accueil tactile est le point de départ de l'équipe (icône de l'iPad, tuiles).
  // Dix de ses seize tuiles mènent ICI, dans le tableau de bord — et rien n'y
  // ramenait : en PWA il n'y a pas de bouton « précédent » de navigateur, donc
  // ouvrir « Produits » depuis l'accueil était un aller sans retour (09/09/2026).
  // Mêmes rôles que la route /accueil, sinon le lien mènerait à un refus.
  const peutAccueil = userRole === 'admin' || userRole === 'manager' || userRole === 'owner';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Obtenir les initiales de l'utilisateur pour l'avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header / TopBar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-30">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Logo + Burger + Tenant Name */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Menu"
              aria-expanded={sidebarOpen}
              className="p-2 rounded-md hover:bg-gray-100 lg:hidden"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {peutAccueil && (
              <Link
                to="/accueil"
                title="Revenir à l'accueil"
                className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 min-h-[40px]"
              >
                <span aria-hidden="true" className="text-lg leading-none">🏠</span>
                <span className="hidden sm:inline text-sm font-medium">Accueil</span>
              </Link>
            )}

            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                F
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-gray-900">
                  {tenant?.name || 'FOLIES SAAS'}
                </h1>
                <p className="text-xs text-gray-500">{tenant?.slug || 'demo'} <span className="text-gray-400">v1.0</span></p>
              </div>
            </Link>
          </div>


          {/* Right: Notifications + User Menu */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative p-2 rounded-md hover:bg-gray-100">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-semibold text-sm">
                  {getUserInitials()}
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700">
                  {user?.name || 'Utilisateur'}
                </span>
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user?.name || 'Utilisateur'}</p>
                    <p className="text-xs text-gray-500">{user?.email || ''}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                      {user?.role || 'USER'}
                    </span>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    👤 Mon profil
                  </Link>
                  {user?.role === 'ADMIN' && (
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      ⚙️ Paramètres
                    </Link>
                  )}
                  <hr className="my-2" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    🚪 Se déconnecter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-gray-200 overflow-y-auto z-20 transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <nav className="p-4 space-y-1">
          {filteredNav.map((item) => (
            <div key={item.name}>
              {/* Parent Item */}
              <Link
                to={item.href}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive(item.href)
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>

              {/* Children Items - Toujours afficher si présents */}
              {item.children && (
                <div className="ml-8 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.href}
                      className={clsx(
                        'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
                        location.pathname === child.href
                          ? 'text-emerald-600 font-medium'
                          : 'text-gray-600 hover:text-gray-900'
                      )}
                    >
                      <span>{child.icon}</span>
                      <span>{child.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

      </aside>

      {/* Main Content */}
      <main
        className={clsx(
          // La sidebar est TOUJOURS visible a partir de lg (lg:translate-x-0),
          // donc le contenu doit TOUJOURS etre decale a lg, independamment de
          // sidebarOpen — sinon apres une rotation portrait->paysage le contenu
          // passe sous la sidebar (bug iPad paysage, 31/08/2026).
          'pt-16 transition-all duration-300',
          'lg:pl-64'
        )}
      >
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-10 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
}
